import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CloudCheck, CloudOff, Download, FilePlus2, FileWarning, FolderOpen, Globe, History, Image as ImageIcon, Info, Loader2, Share2,
  MessageSquareText, Moon, Palette, Play, Presentation, RotateCw, Search, Star, StickyNote, Sun, Undo2, Wand2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { hora12 } from '../../lib/format';
import { urlEnDocs } from '../../lib/dominios';
import { useGuardadoEnDrive } from './guardado';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import OfficeEditor from './OfficeEditor';
import AIComposer, { textosAurora } from './AIComposer';
import AIPanel from './AIPanel';
import DrivePicker from './DrivePicker';
import { createBridge } from './bridge';
import { applyAppLayout, applyEditorTheme, isDarkEditorTheme, rememberEditorTheme } from './skin';
import { runEditorAction } from './editorActions';
import { ejecutar as ejecutarComando } from './editorComandos';
import BarraMovil from './BarraMovil';
import { descargarArchivo } from './descargas';
import { BRAND, OFFICE_APPS, OFFICE_NAME, ProductIcon, AuroraLogo } from './icons';
import AvataresPresencia, { MiAnimal } from './AvataresPresencia';
import { guardarInvitado, invitadoGuardado, usePresencia } from './presencia';
import './office.css';

// Teléfono (o tableta estrecha con dedo): se decide UNA vez al abrir, porque el diseño del motor va en la
// config firmada y cambiarlo exigiría recargar el editor (y salir de la coedición) al girar la pantalla.
function esMovil() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  // `hover: none`: una laptop con pantalla táctil y ratón NO es un teléfono (antes caía en la vista móvil)
  return window.matchMedia('(max-width: 767px), (pointer: coarse) and (hover: none) and (max-width: 1024px)').matches;
}

/**
 * Alto real disponible con el teclado del teléfono abierto (visualViewport). Con él la cabecera y la
 * barra inferior quedan SOBRE el teclado en vez de detrás, y la barra sabe cuándo enseñar la fila rápida.
 */
function useVentanaVisible(activo) {
  const [vv, setVv] = useState(null);
  useEffect(() => {
    const v = window.visualViewport;
    if (!activo || !v) return undefined;
    const medir = () => setVv({ alto: v.height, arriba: v.offsetTop, teclado: window.innerHeight - v.height > 140 });
    medir();
    v.addEventListener('resize', medir);
    v.addEventListener('scroll', medir);
    return () => {
      v.removeEventListener('resize', medir);
      v.removeEventListener('scroll', medir);
    };
  }, [activo]);
  return vv;
}

const ROLE_LABEL = { owner: 'Propietario', editor: 'Editor', commenter: 'Puede comentar', viewer: 'Solo lectura' };

/* O2 (auditoría 2026-10-09): formatos que el motor abre solo para ver y su versión editable. */
const FORMATO_EDITABLE = { doc: '.docx', odt: '.docx', rtf: '.docx', xls: '.xlsx', ods: '.xlsx', ppt: '.pptx', odp: '.pptx', ppsx: '.pptx' };
const RUTA_POR_TIPO = { doc: '/document', sheet: '/spreadsheet', slides: '/presentation' };
const AVATAR_COLORS = ['#2563eb', '#3f9576', '#b07d2e', '#4b7bc0', '#c05c6a', '#7a68d4', '#2f9170', '#9c6ac8'];

function initials(name = '') {
  const parts = name.replace(/[^\p{L}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function avatarColor(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function SlidesRail({ onCompose, onEditor }) {
  const items = [
    { label: 'Diapositiva', icon: FilePlus2, ai: true, onClick: () => onCompose('slide') },
    { label: 'Presentación', icon: Presentation, ai: true, onClick: () => onCompose('deck') },
    { label: 'Mejorar', icon: Wand2, ai: true, onClick: () => onCompose('improve') },
    { label: 'Notas', icon: StickyNote, ai: true, onClick: () => onCompose('notes') },
    { label: 'Imagen', icon: ImageIcon, onClick: () => onEditor('image') },
    { label: 'Tema', icon: Palette, onClick: () => onEditor('theme') },
  ];
  return (
    <nav className="zo-rail" aria-label="Herramientas de diapositivas">
      {items.map(({ label, icon: Icon, ai, onClick }) => (
        <button key={label} type="button" className="zo-rail-item" onClick={onClick}>
          <span className="zo-rail-icon">
            <Icon className="h-5 w-5" />
            {ai && <span className="zo-rail-spark"><AuroraLogo size={11} /></span>}
          </span>
          {label}
        </button>
      ))}
    </nav>
  );
}

// Mientras el motor no tenga licencia de marca blanca, sus términos exigen mostrar el aviso de origen y licencia
function LicenseNotice() {
  return (
    <>
      <p>
        {OFFICE_NAME} ({OFFICE_APPS.doc.name}, {OFFICE_APPS.sheet.name} y {OFFICE_APPS.slides.name}) está basado en{' '}
        <strong>ONLYOFFICE Docs</strong> Community Edition 9.4, desarrollado por Ascensio System SIA.
      </p>
      <p>
        Esta es una <strong>versión modificada</strong> (septiembre de 2026): interfaz, tema visual e integración con
        Aurora IA mediante un plugin.
      </p>
      <p>
        ONLYOFFICE Docs se distribuye bajo la licencia GNU AGPL v3 con términos adicionales. ONLYOFFICE es marca de
        Ascensio System SIA.
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><a href="https://github.com/ONLYOFFICE/DocumentServer" target="_blank" rel="noreferrer">Código fuente de ONLYOFFICE Docs</a></li>
        <li><a href="https://www.gnu.org/licenses/agpl-3.0.html" target="_blank" rel="noreferrer">Licencia GNU AGPL v3</a></li>
        <li><a href="https://www.onlyoffice.com/trademark-policy" target="_blank" rel="noreferrer">Política de marca de ONLYOFFICE</a></li>
      </ul>
    </>
  );
}


/** Editor de Drive: interfaz tipo Google Docs/Sheets/Slides alrededor del motor de edición con coedición. */
/* Publicado en Internet, el botón lo dice (P6, auditoría 2026-09-17), como en el editor clásico:
   aquí el dueño veía «Compartir» neutro aunque el archivo estuviera abierto a cualquiera. */
const ESTILO_PUBLICO = { background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)' };

export default function OfficeShell({
  fileId, meta, kind, user, dark, actionLink, onShare, onClassic, onUnavailable, onDownload, onRenamed, onThemeChange,
  linkToken, guest = false, publico = false,
}) {
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [movil] = useState(esMovil);
  const ventana = useVentanaVisible(movil);
  const [barraCompleta, setBarraCompleta] = useState(false);
  const [descargas, setDescargas] = useState([]); // formatos que el backend ofrece para este archivo
  const app = OFFICE_APPS[kind] || OFFICE_APPS.doc;
  const bridge = useMemo(() => createBridge(), []);
  const iframeRef = useRef(null);
  // Quién más tiene abierto el documento. Un invitado espera a tener su animal (lo da la config del
  // editor) para que los avatares y el nombre dentro del editor digan lo mismo.
  const [invitado, setInvitado] = useState(() => (guest ? invitadoGuardado(fileId) : null));
  const { yo, presentes } = usePresencia({ fileId, linkToken, invitado, activo: !guest || !!invitado });

  const ext = (meta.name.match(/\.[^.]+$/) || [''])[0];
  const baseName = meta.name.slice(0, meta.name.length - ext.length);
  const [title, setTitle] = useState(baseName);
  const [starred, setStarred] = useState(!!meta.starred);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [composerRequest, setComposerRequest] = useState(null);
  const [auroraAbierta, setAuroraAbierta] = useState(false); // O10: la barra de Aurora se abre desde la cabecera
  const [editorNonce, setEditorNonce] = useState(0); // al cerrar el historial o restaurar se vuelve a abrir el editor
  const [picker, setPicker] = useState(null); // { mode, resolve }
  // O1: si el editor no termina de cargar se reintenta solo una vez; si vuelve a fallar, se ofrece reintentar
  const [falloCarga, setFalloCarga] = useState(false);
  const reintentosCarga = useRef(0);
  const [creandoCopia, setCreandoCopia] = useState(false);
  // O7/O8/O9: Ctrl+S desde cualquier parte y «Guardado en Drive · hora» solo cuando Drive cambió
  const guardado = useGuardadoEnDrive({ fileId, linkToken, iframeRef, ready, canEdit });
  const viewOnlyKind = kind === 'diagram';
  // Aurora IA solo para quien entró con su cuenta: nunca en un enlace público
  const conAurora = !viewOnlyKind && !guest && !!user;
  const barraAurora = ready && conAurora && (canEdit || kind === 'pdf');
  const edicionMovil = canEdit && ['doc', 'sheet', 'slides'].includes(kind);

  useEffect(() => () => bridge.dispose(), [bridge]);

  // Tema dentro del archivo: el portal manda y el editor lo sigue sin recargarse
  const darkRef = useRef(dark);
  darkRef.current = dark;
  useEffect(() => {
    rememberEditorTheme(dark);
    if (!ready) return undefined;
    let tries = 0;
    let timer = null;
    const apply = () => {
      if (applyEditorTheme(iframeRef.current, darkRef.current) || tries >= 20) return;
      tries += 1;
      timer = setTimeout(apply, 250);
    };
    apply();
    return () => clearTimeout(timer);
  }, [dark, ready]);

  // Y al revés: si alguien cambia el tema desde Archivo → Configuración avanzada del editor, el portal lo sigue
  const themeHandler = useRef(onThemeChange);
  themeHandler.current = onThemeChange;
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'ui-theme-id' || !e.newValue) return;
      const next = isDarkEditorTheme(e.newValue);
      if (next !== darkRef.current) themeHandler.current?.(next ? 'dark' : 'light');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const toggleTheme = () => onThemeChange?.(dark ? 'light' : 'dark');
  const themeLabel = dark ? 'Tema claro' : 'Tema oscuro';
  useEffect(() => setTitle(baseName), [baseName]);
  useEffect(() => {
    document.title = `${meta.name} · ${app.name}`;
  }, [meta.name, app.name]);

  // Escape cancela (D8): antes ponía el nombre original y luego `blur` llamaba a commitTitle con el
  // título EDITADO de ese render, así que renombraba. La ref le dice a commitTitle que no guarde.
  const tituloCancelado = useRef(false);
  const commitTitle = async () => {
    if (tituloCancelado.current) { tituloCancelado.current = false; setTitle(baseName); return; }
    const clean = title.trim();
    if (!clean || clean === baseName) {
      setTitle(baseName);
      return;
    }
    try {
      await api.put(`/drive/files/${fileId}`, { name: clean + ext });
      onRenamed?.(clean + ext);
    } catch {
      toast.error('No se pudo cambiar el nombre');
      setTitle(baseName);
    }
  };

  const toggleStar = async () => {
    const next = !starred;
    setStarred(next);
    try {
      await api.post(`/drive/files/${fileId}/star`, null, { params: { starred: next } });
    } catch {
      setStarred(!next);
      toast.error('No se pudo actualizar');
    }
  };

  const reloadEditor = () => {
    setReady(false);
    setSaving(false);
    setEditorNonce((n) => n + 1);
  };

  const pickFromDrive = (mode) => new Promise((resolve) => setPicker({ mode, resolve }));
  const closePicker = (choice) => {
    picker?.resolve(choice || null);
    setPicker(null);
  };

  const editorAction = async (action) => {
    if (await runEditorAction(iframeRef.current, action)) return;
    if (action === 'history') toast.message('Abre Colaboración → Historial de versiones');
    else if (action === 'image') toast.message('Usa Insertar → Imagen en la barra del editor');
    else toast.message('Esta opción estará disponible cuando termine de cargar el editor');
  };

  const horaGuardado = guardado.hora ? hora12(guardado.hora) : '';
  const errorGuardado = guardado.estado === 'error' || guardado.estado === 'lento';
  const guardandoAhora = (!ready && !falloCarga) || saving || guardado.estado === 'guardando';
  const mostrarGuardado = !ready || canEdit; // en solo lectura no hay nada que guardar
  const saveLabel = !ready ? (falloCarga ? 'No se pudo cargar' : 'Cargando…')
    : guardado.estado === 'guardando' ? 'Guardando en Drive…'
      : guardado.estado === 'error' ? 'No se pudo guardar en Drive'
        : guardado.estado === 'lento' ? 'El guardado tarda más de lo normal'
          : guardado.estado === 'guardado' ? `Guardado en Drive · ${horaGuardado}`
            : guardado.estado === 'al_dia' && !saving ? `Todo está guardado en Drive${horaGuardado ? ` · ${horaGuardado}` : ''}`
              : saving ? 'Guardando…' : 'Al día en el editor';

  const alFallarCarga = () => {
    if (reintentosCarga.current < 1) {
      reintentosCarga.current += 1;
      reloadEditor();
      return;
    }
    setFalloCarga(true);
  };
  const reintentarCarga = () => {
    setFalloCarga(false);
    reloadEditor();
  };
  // O10: al plegar Aurora el foco vuelve al documento (si no, Ctrl+S y lo que se escribe se quedaban fuera)
  const enfocarDocumento = () => {
    try {
      iframeRef.current?.contentWindow?.focus();
    } catch {
      /* sin editor */
    }
  };

  // O2: un .xls/.doc… abierto solo para ver: se ofrece una copia editable (el original no cambia)
  const formatoNuevo = FORMATO_EDITABLE[ext.slice(1).toLowerCase()];
  const avisoFormatoAntiguo = ready && !canEdit && !!formatoNuevo && !guest && !!user;
  const crearCopiaEditable = async () => {
    setCreandoCopia(true);
    try {
      const { data } = await api.post(`/office/copia-editable/${fileId}`, null, { timeout: 150000 });
      toast.success(`Copia editable creada: ${data.name}`);
      window.location.assign(new URL(urlEnDocs(`${RUTA_POR_TIPO[data.kind] || '/file'}/${data.id}`), window.location.origin).href);
    } catch (err) {
      const detalle = err.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo crear la copia editable. Intenta de nuevo.');
      setCreandoCopia(false);
    }
  };

  const comandoMovil = (cmd, valor) => {
    if (!ejecutarComando(iframeRef.current, cmd, valor)) toast.message('Esa opción no está disponible aquí');
  };
  // Escotilla del teléfono: la cinta completa del motor, para lo que la barra inferior no cubre
  const alternarBarraCompleta = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const next = !barraCompleta;
    doc.documentElement.classList.toggle('orvian-barra', next);
    iframeRef.current.contentWindow?.dispatchEvent(new Event('resize'));
    setBarraCompleta(next);
  };
  // También por el enlace público: el backend revalida el enlace en cada descarga
  const descargarComo = (formato) => descargarArchivo({ fileId, formato, link: linkToken, nombre: meta.name });
  const menuDescargas = descargas.length > 0 && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {guest ? (
          <button type="button" className="zo-btn-outline zo-btn-sm" data-testid="zo-descargar">
            <Download className="h-4 w-4" /> Descargar
          </button>
        ) : (
          <button type="button" className="zo-icon-btn zo-icon-lg" title="Descargar" aria-label="Descargar" data-testid="zo-descargar">
            <Download className="h-5 w-5" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="zo-menu w-72">
        <DropdownMenuLabel className="font-normal text-xs opacity-70">Descargar como</DropdownMenuLabel>
        {descargas.map((d) => (
          <DropdownMenuItem key={d.formato} onSelect={() => descargarComo(d.formato)}>
            <Download className="h-4 w-4 mr-2" /> {d.nombre}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const shellStyle = movil && ventana ? { top: ventana.arriba, height: ventana.alto, bottom: 'auto' } : undefined;
  const roleLabel = role ? (ROLE_LABEL[role] || role) : '';
  const userName = user?.name || user?.email || '';

  return (
    <div
      className={`zo-shell zo-kind-${kind} ${dark ? 'zo-dark' : ''} ${movil ? 'zo-movil' : ''} ${movil && ventana?.teclado ? 'con-teclado' : ''}`}
      style={shellStyle}
      data-testid="office-shell"
    >
      {movil ? (
        <header className="zo-header zo-header-movil">
          {!guest ? (
            <button type="button" className="zo-icon-btn zo-icon-lg" onClick={() => nav('/drive')} aria-label="Volver a Drive" title="Volver a Drive">
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <span className="zo-movil-icono"><ProductIcon kind={kind} size={28} /></span>
          )}
          <div className="zo-titlebox">
            <input
              className="zo-title"
              value={title}
              disabled={!canEdit || guest}
              aria-label="Nombre del archivo"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  tituloCancelado.current = true;
                  setTitle(baseName);
                  e.currentTarget.blur();
                }
              }}
            />
            <div className="zo-movil-sub">
              {(guest || mostrarGuardado) && (
                <>
                  {errorGuardado ? <CloudOff className="h-3 w-3" /> : guardandoAhora ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudCheck className="h-3 w-3" />}
                  <span>
                    {guest ? 'Enlace compartido' : !ready ? (falloCarga ? 'No se pudo cargar' : 'Cargando…') : guardado.estado === 'guardando' ? 'Guardando en Drive…'
                      : errorGuardado ? 'Sin guardar en Drive' : guardado.estado === 'guardado' ? `Guardado · ${horaGuardado}`
                        : saving ? 'Guardando…' : 'Al día'}
                  </span>
                </>
              )}
              {roleLabel && role !== 'owner' && role !== 'editor' && <span className={`zo-role zo-role-${role}`}>{roleLabel}</span>}
              {viewOnlyKind && <span className="zo-role zo-role-viewer">Solo lectura</span>}
              {ready && !canEdit && !viewOnlyKind && ['owner', 'editor'].includes(role) && <span className="zo-role zo-role-viewer">Solo lectura</span>}
            </div>
          </div>
          <AvataresPresencia presentes={presentes} yo={yo} max={1} />
          {!guest && (
            <button type="button" className="zo-share zo-share-movil" onClick={onShare} data-testid="zo-share" title={publico ? 'Publicado en Internet' : 'Compartir'} aria-label={publico ? 'Compartir · publicado en Internet' : 'Compartir'} style={publico ? ESTILO_PUBLICO : undefined}>
              {publico ? <Globe className="h-[18px] w-[18px]" /> : <Share2 className="h-[18px] w-[18px]" />}
            </button>
          )}
          {guest && <MiAnimal yo={yo} />}
        </header>
      ) : (
      <header className="zo-header">
        <button
          type="button"
          className="zo-app-btn"
          onClick={() => !guest && nav('/drive')}
          title={guest ? app.name : `${app.name} · volver a Drive`}
        >
          <ProductIcon kind={kind} size={36} />
        </button>
        <div className="zo-titlebox">
          <div className="zo-title-row">
            <input
              className="zo-title"
              value={title}
              size={Math.max(8, Math.min(title.length + 1, 48))}
              disabled={!canEdit || guest}
              aria-label="Nombre del archivo"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  tituloCancelado.current = true;
                  setTitle(baseName);
                  e.currentTarget.blur();
                }
              }}
            />
            {!guest && role === 'owner' && (   /* solo el dueño destaca (D9) */
              <button type="button" className={`zo-icon-btn ${starred ? 'is-starred' : ''}`} onClick={toggleStar} title={starred ? 'Quitar de destacados' : 'Destacar'} aria-label={starred ? 'Quitar de destacados' : 'Destacar'} aria-pressed={starred}>
                <Star className="h-[18px] w-[18px]" fill={starred ? 'currentColor' : 'none'} />
              </button>
            )}
            {!isMobile && !guest && (
              <button type="button" className="zo-icon-btn" onClick={() => nav('/drive')} title="Ver en Drive" aria-label="Ver en Drive">
                <FolderOpen className="h-[18px] w-[18px]" />
              </button>
            )}
            {errorGuardado ? (
              <button type="button" className="zo-save zo-save-error" onClick={guardado.guardarEnDrive} title="Volver a intentar guardar en Drive (Ctrl+S)" data-testid="zo-guardar-reintentar">
                <CloudOff className="h-4 w-4" />
                <span>{isMobile ? 'Reintentar' : `${saveLabel} · Reintentar`}</span>
              </button>
            ) : mostrarGuardado && (
              <span className="zo-save" title="Todos editan la misma versión en tiempo real. Ctrl+S guarda en Drive al momento; si no, Drive se actualiza al cerrar." data-testid="zo-estado-guardado">
                {guardandoAhora ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudCheck className="h-4 w-4" />}
                {!isMobile && <span>{saveLabel}</span>}
              </span>
            )}
          </div>
          <div className="zo-subtitle">
            <span className="zo-app-name">{app.name}</span>
            {role && <span className={`zo-role zo-role-${role}`}>{ROLE_LABEL[role] || role}</span>}
            {ready && !canEdit && !viewOnlyKind && ['owner', 'editor'].includes(role) && <span className="zo-role zo-role-viewer">Solo lectura</span>}
            {guest && <span className="zo-live">Enlace compartido</span>}
            {/* O12: solo quien puede editar ve «Edición en equipo»; un .xls abierto para ver no la tiene */}
            {!isMobile && !viewOnlyKind && canEdit && <span className="zo-live"><span className="zo-live-dot" /> Edición en equipo en tiempo real</span>}
            {!isMobile && viewOnlyKind && <span className="zo-live">Solo lectura</span>}
          </div>
        </div>

        <div className="zo-actions">
          <AvataresPresencia presentes={presentes} yo={yo} max={isMobile ? 2 : 4} />
          {!isMobile && (
            <button type="button" className="zo-icon-btn zo-icon-lg" onClick={toggleTheme} title={themeLabel} aria-label={themeLabel} data-testid="zo-theme-toggle">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          )}
          {!isMobile && !viewOnlyKind && !guest && (
            <button type="button" className="zo-icon-btn zo-icon-lg" onClick={() => editorAction('history')} title="Historial de versiones" aria-label="Historial de versiones" disabled={!ready} data-testid="zo-history">
              <History className="h-5 w-5" />
            </button>
          )}
          {/* Lectura por enlace en PC: sin cinta del motor, buscar vive aquí */}
          {!isMobile && guest && !canEdit && (
            <button type="button" className="zo-icon-btn zo-icon-lg" onClick={() => editorAction('search')} title="Buscar en el documento" aria-label="Buscar en el documento" disabled={!ready}>
              <Search className="h-5 w-5" />
            </button>
          )}
          {!isMobile && !viewOnlyKind && (!guest || ['commenter', 'editor', 'owner'].includes(role)) && (
            <button type="button" className="zo-icon-btn zo-icon-lg" onClick={() => editorAction('comments')} title="Comentarios" aria-label="Comentarios">
              <MessageSquareText className="h-5 w-5" />
            </button>
          )}
          {kind === 'slides' && (
            <button type="button" className="zo-btn-outline" onClick={() => editorAction('present')} disabled={!ready}>
              <Play className="h-4 w-4" /> {!isMobile && 'Presentar'}
            </button>
          )}
          {menuDescargas}
          {/* O10 (auditoría 2026-10-09): la barra de Aurora se abre desde aquí; antes era una píldora fija encima del documento */}
          {barraAurora && (
            <button
              type="button"
              className="zo-aurora-btn"
              onClick={() => setComposerRequest({ action: auroraAbierta ? 'cerrar' : 'open', nonce: Date.now() })}
              title={textosAurora(kind).completo}
              aria-label={textosAurora(kind).completo}
              aria-expanded={auroraAbierta}
              data-testid="zo-aurora-abrir"
            >
              <AuroraLogo size={18} />
              {!isMobile && <span className="zo-aurora-btn-texto">{textosAurora(kind).corto}</span>}
            </button>
          )}
          {!guest && (
            <button
              type="button"
              className="zo-share"
              onClick={onShare}
              data-testid="zo-share"
              title={publico ? 'Publicado en Internet: cualquiera con el enlace lo abre' : 'Compartir'}
              aria-label={publico ? 'Compartir · publicado en Internet' : 'Compartir'}
              style={publico ? ESTILO_PUBLICO : undefined}
            >
              {publico ? <Globe className="h-4 w-4" /> : <Share2 className="h-4 w-4" />} {!isMobile && (publico ? 'Público' : 'Compartir')}
            </button>
          )}
          {!viewOnlyKind && !guest && (
          <button
            type="button"
            className={`zo-spark-btn ${aiOpen ? 'is-on' : ''}`}
            onClick={() => setAiOpen((v) => !v)}
            title="Conversar con Aurora"
            aria-label="Conversar con Aurora"
            data-testid="zo-ai-toggle"
          >
            <AuroraLogo size={22} />
          </button>
          )}
          {guest ? (
            <>
              {/* En escritorio el botón de tema ya está al principio: aquí solo hace falta en el teléfono */}
              {isMobile && (
                <button
                  type="button"
                  className="zo-icon-btn zo-icon-lg"
                  onClick={toggleTheme}
                  title={themeLabel}
                  aria-label={themeLabel}
                  data-testid="zo-theme-toggle-invitado"
                >
                  {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              )}
              <MiAnimal yo={yo} />
            </>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="zo-avatar" style={{ background: avatarColor(user?.email || userName) }} title={userName} aria-label={`Tu cuenta: ${userName}`}>
                {initials(userName)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="zo-menu w-64">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium truncate">{userName}</div>
                {user?.email && user.email !== userName && <div className="text-xs opacity-70 truncate">{user.email}</div>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={toggleTheme}>
                {dark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />} {themeLabel}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => (descargas.length ? descargarComo('original') : onDownload?.())}><Download className="h-4 w-4 mr-2" /> Descargar</DropdownMenuItem>
              <DropdownMenuItem onSelect={onClassic}><Undo2 className="h-4 w-4 mr-2" /> Abrir en el editor clásico</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </header>
      )}

      {avisoFormatoAntiguo && (
        <div className="zo-aviso" role="status" data-testid="zo-aviso-formato">
          <FileWarning className="h-4 w-4 shrink-0" />
          <span>
            Este archivo es de un formato antiguo ({ext.toLowerCase()}) y solo se puede ver. Para editarlo, crea una copia en {formatoNuevo}: el original no cambia.
          </span>
          <button type="button" className="zo-btn-primary" onClick={crearCopiaEditable} disabled={creandoCopia} data-testid="zo-copia-editable">
            {creandoCopia && <Loader2 className="h-4 w-4 animate-spin" />} Crear copia editable ({formatoNuevo})
          </button>
        </div>
      )}

      <div className="zo-body">
        <div className="zo-stage">
          <OfficeEditor
            key={editorNonce}
            fileId={fileId}
            linkToken={linkToken}
            invitadoId={guest ? invitado?.id : undefined}
            dark={dark}
            actionLink={editorNonce === 0 ? actionLink : undefined}
            // Enlace público en PC: vista de lectura (sin cinta ni reglas) si el enlace no deja editar
            layout={movil ? 'app' : guest ? 'lectura' : undefined}
            onReload={reloadEditor}
            onPick={pickFromDrive}
            onConfig={(cfg) => {
              setRole(cfg.my_role);
              setCanEdit(!!cfg.can_edit);
              setWhiteLabel(!!cfg.white_label);
              setDescargas(Array.isArray(cfg.descargas) ? cfg.descargas : []);
              if (cfg.identidad_invitado) {
                guardarInvitado(fileId, cfg.identidad_invitado);
                setInvitado(cfg.identidad_invitado);
              }
            }}
            onLoadFailed={alFallarCarga}
            onWarning={guardado.alAviso}
            onReady={(iframe) => {
              iframeRef.current = iframe;
              reintentosCarga.current = 0;
              setFalloCarga(false);
              setReady(true);
              setBarraCompleta(false);
              if (movil) {
                // Sin tiras laterales el motor vuelve a medir el lienzo; presentaciones: miniaturas abajo
                applyAppLayout(iframe);
                setTimeout(() => applyAppLayout(iframe), 400);
                // El PDF (y a veces el documento) se queda midiendo el ancho con las tiras aún puestas:
                // se vuelve a medir y a ajustar cuando el motor ya asentó su diseño
                if (kind !== 'sheet') {
                  setTimeout(() => {
                    applyAppLayout(iframe);
                    ejecutarComando(iframe, 'zoomFit');
                  }, 1200);
                }
              }
            }}
            onStateChange={setSaving}
            onUnavailable={onUnavailable}
          />
          {!ready && !falloCarga && (
            <div className="zo-splash">
              <ProductIcon kind={kind} size={56} />
              <div className="zo-splash-name">{app.name}</div>
              <div className="zo-splash-bar"><span /></div>
            </div>
          )}
          {falloCarga && (
            <div className="zo-splash zo-fallo" role="alert" data-testid="zo-fallo-carga">
              <ProductIcon kind={kind} size={56} />
              <div className="zo-splash-name">No se pudo abrir {app.name}</div>
              <p className="zo-fallo-texto">El editor no terminó de cargar. Suele arreglarse al reintentar; el archivo no cambió.</p>
              <div className="zo-fallo-acciones">
                <button type="button" className="zo-btn-primary" onClick={reintentarCarga}>
                  <RotateCw className="h-4 w-4" /> Reintentar
                </button>
                {descargas.length > 0 && (
                  <button type="button" className="zo-btn-outline" onClick={() => descargarComo('original')}>
                    <Download className="h-4 w-4" /> Descargar el archivo
                  </button>
                )}
              </div>
            </div>
          )}
          {barraAurora && (
            <AIComposer kind={kind} fileId={fileId} bridge={bridge} canEdit={canEdit} request={composerRequest} soloAbierto onPlegar={enfocarDocumento} onAbierto={setAuroraAbierta} />
          )}
        </div>
        {ready && kind === 'slides' && canEdit && !isMobile && !guest && (
          <SlidesRail
            onCompose={(action) => setComposerRequest({ action, nonce: Date.now() })}
            onEditor={editorAction}
          />
        )}
        {aiOpen && conAurora && (
          <AIPanel kind={kind} fileId={fileId} bridge={bridge} canEdit={canEdit} userName={userName} onClose={() => setAiOpen(false)} />
        )}
      </div>

      {movil && (
        <BarraMovil
          kind={kind}
          iframe={iframeRef.current}
          ready={ready}
          edicion={edicionMovil}
          comentar={['owner', 'editor', 'commenter'].includes(role) && !viewOnlyKind}
          guest={guest}
          dark={dark}
          starred={starred}
          teclado={!!ventana?.teclado}
          aurora={conAurora}
          barraCompleta={barraCompleta}
          onComando={comandoMovil}
          onAccion={editorAction}
          onPresentar={() => editorAction('present')}
          onTema={toggleTheme}
          onDestacar={role === 'owner' ? toggleStar : undefined}
          descargas={descargas}
          onDescargar={descargarComo}
          onDrive={() => nav('/drive')}
          // Con escritura (o PDF) abre la barra de Aurora; si no, la conversación, que solo lee
          onAurora={() => {
            if (canEdit || kind === 'pdf') setComposerRequest({ action: 'open', nonce: Date.now() });
            else setAiOpen(true);
          }}
          onAuroraChat={() => setAiOpen(true)}
          onBarraCompleta={canEdit && !guest ? alternarBarraCompleta : null}
        />
      )}

      {picker && <DrivePicker mode={picker.mode} onSelect={closePicker} onCancel={() => closePicker(null)} />}
    </div>
  );
}
